import { useMemo } from 'react';
import { Box, Card, CardContent, CircularProgress, Grid2, Typography, alpha, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { Error as ErrorIcon, Block as BlockIcon, Warning as WarningIcon } from '@mui/icons-material';

interface ErrorCategory {
  blocked?: number | null;
  poorReputation?: number | null;
  fairReputation?: number | null;
}

interface ErrorBreakdown {
  totalFailures?: number | null;
  categories?: ErrorCategory | null;
  errorRate?: number | null;
}

interface ErrorMetricsPanelProps {
  data?: ErrorBreakdown | null;
  loading?: boolean;
  error?: Error | null;
}

const ErrorMetricsPanel = ({ data, loading, error }: ErrorMetricsPanelProps) => {
  const theme = useTheme();

  const pieChartData = useMemo(() => {
    if (!data?.categories) return [];

    const categories = data.categories;
    const chartData = [];

    if ((categories.blocked ?? 0) > 0) {
      chartData.push({
        id: 'blocked',
        value: categories.blocked ?? 0,
        label: 'Blocked',
        color: '#F44336',
      });
    }

    if ((categories.poorReputation ?? 0) > 0) {
      chartData.push({
        id: 'poor',
        value: categories.poorReputation ?? 0,
        label: 'Poor Reputation',
        color: '#FF9800',
      });
    }

    if ((categories.fairReputation ?? 0) > 0) {
      chartData.push({
        id: 'fair',
        value: categories.fairReputation ?? 0,
        label: 'Fair Reputation',
        color: '#FFC107',
      });
    }

    return chartData;
  }, [data]);

  const totalErrors = useMemo(() => {
    return pieChartData.reduce((sum, item) => sum + item.value, 0);
  }, [pieChartData]);

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <Typography variant="h6" color="error" gutterBottom>
              Error loading error metrics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data || totalErrors === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <ErrorIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No errors detected
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              All domains are performing well
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 350,
        borderRadius: 2,
        boxShadow: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 'none',
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary.dark">
              Error Breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Distribution of crawler errors by category
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="h4" fontWeight={700} color="error.main">
              {data?.errorRate?.toFixed(1) ?? '0'}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Error Rate
            </Typography>
          </Box>
        </Box>

        <Grid2 container spacing={2}>
          {/* Pie Chart */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box display="flex" justifyContent="center" alignItems="center" height={250}>
              <PieChart
                series={[
                  {
                    data: pieChartData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    innerRadius: 50,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                width={300}
                height={250}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
            </Box>
          </Grid2>

          {/* Legend and Stats */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box display="flex" flexDirection="column" gap={2} pt={2}>
              {/* Total Failures */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    Total Failures
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {data?.totalFailures?.toLocaleString() ?? '0'}
                </Typography>
              </Box>

              {/* Category Breakdown */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {pieChartData.map(item => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(item.color, 0.05),
                      border: `1px solid ${alpha(item.color, 0.2)}`,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: item.color }}>
                      {item.value.toLocaleString()} ({((item.value / totalErrors) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default ErrorMetricsPanel;
